import { useCallback, useMemo } from 'react';

import { openCitationReviewWizard } from './openCitationReviewWizard';
import { useCitationRequests } from './useCitationRequests';
import { useStatementReferenceState } from './useStatementReferences';
import { type CitationRequest, isReviewableStatus } from '../model/citationRequest';

/**
 * The reviewable citation request the callout should open — the message where
 * Primum has found references and they're ready to review. Prefers a freshly
 * ready `awaiting_review` request, else the most recent reviewable one
 * (`published` counts as reviewable). Kept in step with the equivalent picker in
 * `useAutoOpenReferencesReady`; both must select the same request so the callout
 * and the deep-link open land on the same surface.
 */
function pickReviewableRequest(requests: readonly CitationRequest[]): CitationRequest | undefined {
  const reviewable = requests.filter((request) => isReviewableStatus(request.status));

  if (reviewable.length === 0) {
    return undefined;
  }

  const awaitingReview = reviewable.filter((request) => request.status === 'awaiting_review');

  const pool = awaitingReview.length > 0 ? awaitingReview : reviewable;

  return pool.reduce((latest, request) =>
    request.updatedAt > latest.updatedAt ? request : latest
  );
}

type UseReferenceAssistantIntroInput = {
  conversationId: string;
  /** Caller-side eligibility (expert who has responded). Gates the request query. */
  enabled: boolean;
};

/**
 * Backs the reference-assistant intro callout. The callout only makes sense when
 * the expert can actually act on it, so this reports whether Primum has found
 * references ready to review for a message in this conversation, and exposes a
 * handler that opens the citation wizard for that message.
 *
 * Targets the reviewable citation request (not just any message with extracted
 * statements) for two reasons: it is the message the "Select references" button
 * appears on, and on web the wizard only opens for a message whose bubble is
 * mounted — the ready-to-review message reliably is. This mirrors
 * `useAutoOpenReferencesReady`, the proven programmatic open, reading the
 * message's statements from the conversation's statement references.
 */
export function useReferenceAssistantIntro({
  conversationId,
  enabled,
}: UseReferenceAssistantIntroInput) {
  const { data: citationRequests } = useCitationRequests(conversationId, { enabled });

  const reviewableRequest = useMemo(
    () => (citationRequests ? pickReviewableRequest(citationRequests) : undefined),
    [citationRequests]
  );

  const targetState = useStatementReferenceState(conversationId, reviewableRequest?.messageSid, {
    enabled,
  });

  const openWizard = useCallback(() => {
    if (!reviewableRequest) {
      return;
    }

    openCitationReviewWizard({
      conversationId,
      messageSid: reviewableRequest.messageSid,
      extractedStatements: targetState?.extractedStatements ?? [],
      statementCitationStatuses: targetState?.statementCitationStatuses,
      attachedStatementReferences: targetState?.attachedStatementReferences ?? [],
      statementReferenceHelpRequests: targetState?.statementReferenceHelpRequests,
      citationRequest: reviewableRequest,
      openSource: 'reference_assistant_callout',
    });
  }, [conversationId, reviewableRequest, targetState]);

  return { hasReviewableReferences: !!reviewableRequest, openWizard };
}
